import { EmployeeNameDocument, EmployeeSelectorDocument } from 'generated/resources/graphql'

import { createGraphqlDataSourceDefinition } from 'modules/core/hooks/useGraphqlDataSource'
import buildUrl from 'modules/core/lib/buildUrl'
import { getEmployeeDisplayName } from 'modules/core/lib/employeeName'
import i18n from 'modules/core/lib/i18n'
import { createBreadcrumbSelectDefinition } from 'modules/core/lib/navigation'
import { MetaContext, MetaValue } from 'modules/core/lib/navigation/routes'
import { ResourceMeta } from 'modules/core/lib/navigation/types'

export const buildMyProjectsManagerMeta = (context: MetaContext): MetaValue<ResourceMeta> => {
  const { watchQuery, params, session } = context
  const employeeId = Number(params.employeeId)
  const companyId = session.company.id
  const year = params.year ?? ''
  const month = params.month ?? ''
  const day = params.day ?? ''
  const { navigation } = context

  if (!employeeId) {
    return { title: '' }
  }

  return watchQuery({
    query: EmployeeNameDocument,
    variables: { employeeId },
  }).map(({ data }) => {
    const employee = data.employees.employee
    if (!employee) {
      return { title: '' }
    }

    const employeeDisplayFullName = getEmployeeDisplayName({
      fullName: employee.fullName ?? employee.access?.fullName ?? '',
      lastName: employee.lastName ?? employee.access?.lastName ?? '',
      preferredName: employee.preferredName,
    })
    const employeeName = employee.access?.fullName ?? employeeDisplayFullName
    const pageTitle = i18n.t('project_management.meta_data.employee_projects_page.title', {
      employee_name: employeeName,
    })

    const source = createGraphqlDataSourceDefinition({
      document: EmployeeSelectorDocument,
      mapVariables: ({ search }) => ({
        companyId,
        active: true,
        onlyManagers: false,
        search: search ?? '',
        excludeIds: [],
      }),
      mapData: (queryData) => {
        return queryData.employees.employeeNamesConnection.edges.map((edge) => edge.node)
      },
      mapPagination: (queryData) => {
        const connection = queryData.employees.employeeNamesConnection
        const pageInfo = connection.pageInfo
        return {
          total: 0,
          cursor: pageInfo.endCursor ?? '0',
          hasNextPage: pageInfo.hasNextPage ?? false,
        }
      },
      paginationType: 'infinite-scroll',
    })

    const breadcrumbSelect = createBreadcrumbSelectDefinition<
      ReturnType<typeof source.mapData>[number],
      typeof source
    >({
      url: (value: string) =>
        navigation.timeTracking.employee.myProjects.yearMonthDayEmployeeId.fullPath({
          year,
          month,
          day,
          employeeId: value,
        }),
      mapOptions: (employeeItem) => ({
        label: getEmployeeDisplayName({
          fullName: employeeItem.fullName ?? '',
          lastName: employeeItem.lastName ?? '',
          preferredName: employeeItem.preferredName,
        }),
        value: employeeItem.id.toString(),
        avatar: {
          type: 'person' as const,
          firstName: (employeeItem.preferredName || employeeItem.firstName) ?? '',
          lastName: employeeItem.lastName ?? '',
          src: buildUrl(employeeItem.access?.avatar) || undefined,
        },
      }),
      source,
      defaultItem: {
        label: employeeDisplayFullName,
        value: employee.id.toString(),
        avatar: {
          type: 'person' as const,
          firstName:
            (employee.preferredName || employee.firstName || employee.access?.firstName) ?? '',
          lastName: employee.lastName ?? employee.access?.lastName ?? '',
          src: buildUrl(employee.access?.avatar ?? employee.avatar?.url) || undefined,
        },
      },
    })

    return {
      title: pageTitle,
      description: i18n.t('project_management.meta_data.employee_projects_page.description', {
        employee_name: employeeName,
      }),
      headerComponentProps: {
        title: pageTitle,
        avatar: {
          src: buildUrl(employee.avatar?.url ?? employee.access?.avatar) || undefined,
          firstName:
            (employee.preferredName || employee.firstName || employee.access?.firstName) ?? '',
          lastName: employee.lastName ?? employee.access?.lastName ?? '',
          type: 'person',
        },
      },
      siblingsNavigation: {
        select: breadcrumbSelect,
      },
    }
  })
}
